import axios from "axios";

// API 기본 설정
const BASE_URL = "https://www.binaida.net";
const API_PREFIX = "/api/";
const API_BASE_URL = BASE_URL + API_PREFIX;

// 요청 타임아웃 설정
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30초
  headers: {
    "Content-Type": "application/json",
  },
});

// 인터셉터 설정 - 요청 토큰 추가
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 인터셉터 설정 - 응답 오류 처리
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem("refreshToken");
        const res = await axios.post(`${API_BASE_URL}user/token/`, {
          refresh: refreshToken,
        });
        localStorage.setItem("accessToken", res.data.access);
        return axiosInstance(originalRequest);
      } catch (err) {
        // 토큰 재발급 실패 시 로그아웃 처리
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  signIn: (credentials) => axiosInstance.post("auth/sign-in/", credentials),
  signOut: () => axiosInstance.post("auth/sign-out/"),
  signUp: (userData) => axiosInstance.post("auth/sign-up/", userData),
  authKakao: (data) =>
    axiosInstance.post("auth/kakao/", {
      kakao_access_token: data.kakaoAccessToken,
      // 필요하다면 device_token도 추가: device_token: data.deviceToken
    }),
  verifyToken: (token) => axiosInstance.post("auth/verify/", { token }),
  // 휴대폰 인증 요청
  verify: async (phone) => {
    console.log("phone", phone);
    try {
      const response = await axiosInstance.post("auth/verify/", {
        phone: phone,
      });      
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // 휴대폰 인증번호 검증
  verifyCode: async (phone, code) => {
    try {
      const response = await axiosInstance.post("auth/verify-check/", {
        phone: phone,
        code: code,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },


};

// User API
export const userAPI = {
  getProfile: () => axiosInstance.get("user/"),
  updateProfile: (userData) => axiosInstance.patch("user/", userData),
  reIssueToken: (refreshToken) =>
    axiosInstance.post("user/token/", { refresh: refreshToken }),
  getAllUsers: () => axiosInstance.get("user/userview/"),
  updateUserById: (id, data) => axiosInstance.patch(`user/${id}/`, data),
  deleteUserById: (id) => axiosInstance.delete(`user/${id}/`),
};

// Buddhism API
export const buddhismAPI = {
  getAll: (params = {}) => axiosInstance.get("buddhism/", { params }),
  getById: (id) => axiosInstance.get(`buddhism/${id}/`),
  create: (data) => axiosInstance.post("buddhism/", data),
  update: (id, data) => axiosInstance.put(`buddhism/${id}/`, data),
  patch: (id, data) => axiosInstance.patch(`buddhism/${id}/`, data),
  delete: (id) => axiosInstance.delete(`buddhism/${id}/`),
  getSects: () => axiosInstance.get("buddhism/sect/?limit=10000"),
};

// Community API
export const communityAPI = {
  getAll:  (params = {}) => axiosInstance.get("community/", { params }),
  getById: (id)          => axiosInstance.get(`community/${id}/`),
  create:  (data)        => axiosInstance.post("community/", data),
  update:  (id, data)    => axiosInstance.patch(`community/${id}/`, data),
  delete:  (id)          => axiosInstance.delete(`community/${id}/`),
  join:    (id)          => axiosInstance.post(`community/${id}/join/`),
  leave:   (id)          => axiosInstance.post(`community/${id}/leave/`),
  members: (id)          => axiosInstance.get(`community/${id}/members/`),
};

// Order API
export const orderAPI = {
  getAll: (params = {}) => axiosInstance.get("order/", { params }),
  getById: (id) => axiosInstance.get(`order/${id}/`),
  create: (orderData) => axiosInstance.post("order/", orderData),
  update: (id, orderData) => axiosInstance.put(`order/${id}/`, orderData),
  patch: (id, orderData) => axiosInstance.patch(`order/${id}/`, orderData),
  done: (id, formData) => axiosInstance.post(`order/${id}/done/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => axiosInstance.delete(`order/${id}/`),
};

// Notice API
export const noticeAPI = {
  getAll: (params = {}) => axiosInstance.get("notice/", { params }),
  getById: (id) => axiosInstance.get(`notice/${id}/`),
  create: (noticeData) => axiosInstance.post("notice/", noticeData),
  update: (id, noticeData) => axiosInstance.put(`notice/${id}/`, noticeData),
  patch: (id, noticeData) => axiosInstance.patch(`notice/${id}/`, noticeData),
  delete: (id) => axiosInstance.delete(`notice/${id}/`),
};

// Banner API
export const bannerAPI = {
  getAll: () => axiosInstance.get("banner/"),
  getById: (id) => axiosInstance.get(`banner/${id}/`),
  create: (bannerData) => axiosInstance.post("banner/", bannerData),
  update: (id, bannerData) => axiosInstance.put(`banner/${id}/`, bannerData),
  delete: (id) => axiosInstance.delete(`banner/${id}/`),
};

// Message API
export const messageAPI = {
  getHistory: () => axiosInstance.get("messagehistory/"),
  getById: (id) => axiosInstance.get(`messagehistory/${id}/`),
  create: (messageData) => axiosInstance.post("messagehistory/", messageData),
  update: (id, messageData) =>
    axiosInstance.put(`messagehistory/${id}/`, messageData),
  delete: (id) => axiosInstance.delete(`messagehistory/${id}/`),
};

export const imageAPI = {
  upload: async (formData, parents = 'common') => {
    const token = localStorage.getItem("accessToken");
    const response = await fetch(`${API_BASE_URL}common/image/?parents=${parents}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });
    if (!response.ok) {
      let errDetail = response.statusText;
      try {
        const errBody = await response.json();
        errDetail = JSON.stringify(errBody);
      } catch (e) {
        errDetail = await response.text();
      }
      throw new Error(`Upload failed: ${errDetail}`);
    }
    const data = await response.json();
    return { data };
  },
  getById: (id) => axiosInstance.get(`common/image/${id}/`),
  delete: (id) => axiosInstance.delete(`common/image/${id}/`),
};

export default axiosInstance;
